import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/app/StatusPill';
import { Building2, MapPin, Search, Plus, Pencil, Trash2, ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth';
import { formatINR } from '@/lib/format';
import { toast } from 'sonner';
import { PropertyMedia } from '@/components/app/PropertyMedia';
import { PropertyDocuments } from '@/components/app/PropertyDocuments';
import { NotesPanel } from '@/components/app/NotesPanel';
import { PROPERTY_TYPES, PROPERTY_TYPE_LABEL, FURNISHING_OPTIONS } from '@/lib/constants';

const EMPTY_PROP = {
  code: '', name: '', type: 'residential', address: '', city: '', state: '', pin_code: '', survey_number: '',
  owner_id: '', consultant_id: '', agent_id: '',
  total_units: 0, status: 'active', image_url: '',
  classification: '', carpet_area: null, built_up_area: null, floor_details: '',
  furnishing: '', description: '', amenities: [],
};
const EMPTY_UNIT = { property_id: '', unit_number: '', area_sqft: 0, floor: '', configuration: '', rent_amount: 0, deposit_amount: 0, status: 'vacant' };

export default function PropertiesList() {
  const { user } = useAuth();
  const canManage = user && ['owner', 'consultant', 'admin'].includes(user.role);

  const [props, setProps] = useState([]);
  const [owners, setOwners] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);   // property being created/edited
  const [unitEdit, setUnitEdit] = useState(null); // unit being created/edited

  const load = () => api.get('/properties', { params: { q: q || undefined } }).then((r) => setProps(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'consultant') api.get('/consultants').then(r => setConsultants(r.data)).catch(() => {});
    if (user?.role === 'admin' || user?.role === 'consultant') api.get('/owners').then(r => setOwners(r.data)).catch(() => {});
    if (user?.role === 'admin') api.get('/agents').then(r => setAgents(r.data)).catch(() => {});
  }, [user]);

  const startNew = () => {
    const init = { ...EMPTY_PROP };
    if (user.role === 'owner') init.owner_id = user.profile_id;
    setEdit(init);
  };

  const saveProperty = async () => {
    try {
      const payload = {
        ...edit,
        total_units: Number(edit.total_units || 0),
        carpet_area: edit.carpet_area === '' ? null : edit.carpet_area,
        built_up_area: edit.built_up_area === '' ? null : edit.built_up_area,
        consultant_id: edit.consultant_id || null,
        agent_id: edit.agent_id || null,
        amenities: edit.amenities || [],
      };
      if (edit.id) await api.put(`/properties/${edit.id}`, payload);
      else await api.post('/properties', payload);
      toast.success(edit.id ? 'Property updated' : 'Property created');
      setEdit(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const deleteProperty = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and all its units, media, and references? This cannot be undone.`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success('Property deleted'); setOpen(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <PageHeader
        title="Properties"
        subtitle={canManage ? 'Browse, add, edit and manage properties + their media' : 'Browse the portfolio and drill into units'}
        actions={canManage && <Button onClick={startNew} data-testid="add-property-button"><Plus className="h-4 w-4 mr-1" /> New property</Button>}
      />
      <div className="mb-4 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search by name, code, address" value={q} onChange={(e) => setQ(e.target.value)} data-testid="properties-search" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {props.map((p) => (
          <Card key={p.id} className="card-hover overflow-hidden" data-testid={`property-card-${p.id}`}>
            <div className="h-32 w-full bg-muted overflow-hidden cursor-pointer" onClick={() => setOpen(p)}>
              {p.image_url ? <img src={p.image_url} className="h-full w-full object-cover" alt="" /> : <div className="h-full grid place-items-center text-muted-foreground"><Building2 className="h-7 w-7" /></div>}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="cursor-pointer" onClick={() => setOpen(p)}>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{p.code}</div>
                </div>
                <StatusPill status={p.type} label={PROPERTY_TYPE_LABEL[p.type] || p.type} />
              </div>
              <div className="text-xs text-muted-foreground mt-2 flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5" /> <span className="line-clamp-2">{p.address}</span></div>
              <div className="text-xs mt-3 flex items-center justify-between">
                <span><span className="text-muted-foreground">Units:</span> <span className="font-medium">{p.total_units}</span></span>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEdit(p)} data-testid={`edit-property-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteProperty(p)} data-testid={`delete-property-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
        {!props.length && <div className="col-span-full text-center text-sm text-muted-foreground py-12">No properties to show.</div>}
      </div>

      {/* Property detail dialog with Units + Media */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="!max-w-4xl max-h-[92dvh] overflow-auto" data-testid="property-detail-dialog">
          <DialogHeader>
            <DialogTitle>{open?.name} · <span className="font-mono text-sm text-muted-foreground">{open?.code}</span></DialogTitle>
          </DialogHeader>
          {open && (
            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="units">Units</TabsTrigger>
                <TabsTrigger value="media"><ImageIcon className="h-3.5 w-3.5 mr-1" /> Media</TabsTrigger>
                <TabsTrigger value="documents" data-testid="property-documents-tab">Documents</TabsTrigger>
                {(user?.role === 'admin' || user?.role === 'consultant' || user?.role === 'owner') && (
                  <TabsTrigger value="notes">Notes</TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="overview" className="text-sm space-y-2 mt-3">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {open.address}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                  <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Type</div><div className="font-medium">{PROPERTY_TYPE_LABEL[open.type] || open.type}</div></div>
                  <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Status</div><div className="capitalize font-medium">{open.status}</div></div>
                  <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">City</div><div className="font-medium">{open.city || '—'}</div></div>
                  <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Total units</div><div className="font-medium">{open.total_units}</div></div>
                  {open.state && <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">State</div><div className="font-medium">{open.state}</div></div>}
                  {open.pin_code && <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">PIN</div><div className="font-medium">{open.pin_code}</div></div>}
                  {open.carpet_area && <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Carpet area</div><div className="font-medium">{open.carpet_area} sqft</div></div>}
                  {open.built_up_area && <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Built-up area</div><div className="font-medium">{open.built_up_area} sqft</div></div>}
                  {open.furnishing && <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Furnishing</div><div className="capitalize font-medium">{open.furnishing.replace('_', ' ')}</div></div>}
                  {open.floor_details && <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Floor details</div><div className="font-medium">{open.floor_details}</div></div>}
                </div>
                {open.amenities && open.amenities.length > 0 && (
                  <div className="mt-3"><div className="text-xs text-muted-foreground mb-1">Amenities</div><div className="flex flex-wrap gap-1.5">{open.amenities.map((a, i) => <span key={`${a}-${i}`} className="px-2 py-1 rounded-md border bg-muted/40 text-xs">{a}</span>)}</div></div>
                )}
                {open.description && <div className="mt-3"><div className="text-xs text-muted-foreground mb-1">Description</div><div className="text-sm">{open.description}</div></div>}
              </TabsContent>
              <TabsContent value="units" className="mt-3">
                <UnitsTab propertyId={open.id} canManage={canManage} onUnitEdit={setUnitEdit} />
              </TabsContent>
              <TabsContent value="media" className="mt-3">
                <PropertyMedia propertyId={open.id} canManage={canManage && (user.role === 'admin' || (user.role === 'owner' && open.owner_id === user.profile_id) || (user.role === 'consultant' && open.consultant_id === user.profile_id))} />
              </TabsContent>
              <TabsContent value="documents" className="mt-3">
                <PropertyDocuments propertyId={open.id} canManage={canManage && (user.role === 'admin' || (user.role === 'owner' && open.owner_id === user.profile_id) || (user.role === 'consultant' && open.consultant_id === user.profile_id))} />
              </TabsContent>
              {(user?.role === 'admin' || user?.role === 'consultant' || user?.role === 'owner') && (
                <TabsContent value="notes" className="mt-3">
                  <NotesPanel propertyId={open.id} />
                </TabsContent>
              )}
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Property create/edit dialog */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="!max-w-2xl max-h-[92dvh] overflow-y-auto" data-testid="property-edit-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? 'Edit property' : 'New property'}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div><Label>Code <span className="text-destructive">*</span></Label><Input value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} data-testid="property-code-input" /></div>
              <div><Label>Name <span className="text-destructive">*</span></Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="property-name-input" /></div>
              <div>
                <Label>Type</Label>
                <Select value={edit.type} onValueChange={(v) => setEdit({ ...edit, type: v })}>
                  <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Classification</Label>
                <Select value={edit.classification || ''} onValueChange={(v) => setEdit({ ...edit, classification: v })}>
                  <SelectTrigger><SelectValue placeholder="(optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Address <span className="text-destructive">*</span></Label><Input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} data-testid="property-address-input" /></div>
              <div><Label>City</Label><Input value={edit.city || ''} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={edit.state || ''} onChange={(e) => setEdit({ ...edit, state: e.target.value })} /></div>
              <div><Label>PIN code</Label><Input value={edit.pin_code || ''} onChange={(e) => setEdit({ ...edit, pin_code: e.target.value })} /></div>
              <div><Label>Survey / plot #</Label><Input value={edit.survey_number || ''} onChange={(e) => setEdit({ ...edit, survey_number: e.target.value })} /></div>
              <div><Label>Carpet area (sqft)</Label><Input type="number" value={edit.carpet_area || ''} onChange={(e) => setEdit({ ...edit, carpet_area: e.target.value === '' ? null : Number(e.target.value) })} /></div>
              <div><Label>Built-up area (sqft)</Label><Input type="number" value={edit.built_up_area || ''} onChange={(e) => setEdit({ ...edit, built_up_area: e.target.value === '' ? null : Number(e.target.value) })} /></div>
              <div><Label>Floor details</Label><Input value={edit.floor_details || ''} onChange={(e) => setEdit({ ...edit, floor_details: e.target.value })} placeholder="G+5, 4th of 12" /></div>
              <div>
                <Label>Furnishing</Label>
                <Select value={edit.furnishing || ''} onValueChange={(v) => setEdit({ ...edit, furnishing: v })}>
                  <SelectTrigger><SelectValue placeholder="(optional)" /></SelectTrigger>
                  <SelectContent>
                    {FURNISHING_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {user?.role !== 'owner' && (
                <div><Label>Owner</Label><Select value={edit.owner_id || ''} onValueChange={(v) => setEdit({ ...edit, owner_id: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{owners.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
              )}
              <div><Label>Consultant</Label><Select value={edit.consultant_id || ''} onValueChange={(v) => setEdit({ ...edit, consultant_id: v === '_none_' ? null : v })}><SelectTrigger><SelectValue placeholder="(optional)" /></SelectTrigger><SelectContent><SelectItem value="_none_">— None —</SelectItem>{consultants.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              {user?.role === 'admin' && (
                <div><Label>Agent</Label><Select value={edit.agent_id || ''} onValueChange={(v) => setEdit({ ...edit, agent_id: v === '_none_' ? null : v })}><SelectTrigger><SelectValue placeholder="(optional)" /></SelectTrigger><SelectContent><SelectItem value="_none_">— None —</SelectItem>{agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
              )}
              <div><Label>Status</Label><Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select></div>
              <div className="sm:col-span-2"><Label>Description</Label><Input value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Brief description" /></div>
              <div className="sm:col-span-2"><Label>Amenities (comma-separated)</Label><Input value={(edit.amenities || []).join(', ')} onChange={(e) => setEdit({ ...edit, amenities: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="Lift, Gym, Pool, Power Backup" /></div>
              <div className="sm:col-span-2"><Label>Cover image URL (optional)</Label><Input value={edit.image_url || ''} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} placeholder="https://..." /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveProperty} data-testid="property-save-button">{edit?.id ? 'Save changes' : 'Create property'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <UnitEditDialog unit={unitEdit} onClose={() => setUnitEdit(null)} onSaved={() => { setUnitEdit(null); load(); }} />
    </div>
  );
}

function UnitsTab({ propertyId, canManage, onUnitEdit }) {
  const [units, setUnits] = useState([]);
  const load = () => api.get('/units', { params: { property_id: propertyId } }).then(r => setUnits(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [propertyId]);

  const remove = async (u) => {
    if (!window.confirm(`Delete unit ${u.unit_number}?`)) return;
    try {
      await api.delete(`/units/${u.id}`);
      toast.success('Unit deleted'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3">
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => onUnitEdit({ ...EMPTY_UNIT, property_id: propertyId, _onSaved: load })} data-testid="add-unit-button"><Plus className="h-3.5 w-3.5 mr-1" /> Add unit</Button>
        </div>
      )}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Unit</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Config</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Floor</th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Rent</th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Deposit</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Status</th>
              {canManage && <th className="text-right px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2 font-mono">{u.unit_number}</td>
                <td className="px-3 py-2">{u.configuration}</td>
                <td className="px-3 py-2">{u.floor}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(u.rent_amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(u.deposit_amount)}</td>
                <td className="px-3 py-2"><StatusPill status={u.status} /></td>
                {canManage && (
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => onUnitEdit({ ...u, _onSaved: load })}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(u)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </td>
                )}
              </tr>
            ))}
            {!units.length && <tr><td colSpan={canManage ? 7 : 6} className="text-center py-6 text-muted-foreground">No units yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnitEditDialog({ unit, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(unit); }, [unit]);
  if (!form) return null;
  const save = async () => {
    try {
      const payload = { ...form, rent_amount: Number(form.rent_amount || 0), deposit_amount: Number(form.deposit_amount || 0), area_sqft: Number(form.area_sqft || 0) };
      delete payload._onSaved;
      delete payload.id;
      if (form.id) await api.put(`/units/${form.id}`, payload);
      else await api.post('/units', payload);
      toast.success(form.id ? 'Unit updated' : 'Unit created');
      form._onSaved?.();
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Dialog open={!!form} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{form.id ? 'Edit unit' : 'New unit'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><Label>Unit number</Label><Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} data-testid="unit-number-input" /></div>
          <div><Label>Configuration</Label><Input value={form.configuration || ''} onChange={(e) => setForm({ ...form, configuration: e.target.value })} placeholder="2BHK / Office" /></div>
          <div><Label>Floor</Label><Input value={form.floor || ''} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
          <div><Label>Area (sqft)</Label><Input type="number" value={form.area_sqft || 0} onChange={(e) => setForm({ ...form, area_sqft: e.target.value })} /></div>
          <div><Label>Monthly rent (₹)</Label><Input type="number" value={form.rent_amount || 0} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} /></div>
          <div><Label>Deposit (₹)</Label><Input type="number" value={form.deposit_amount || 0} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} /></div>
          <div className="col-span-2"><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="vacant">Vacant</SelectItem><SelectItem value="occupied">Occupied</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent></Select></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="unit-save-button">{form.id ? 'Save changes' : 'Create unit'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
